import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { adminService } from "../../services/adminService";
import { virtualNumberService } from "../../services/virtualNumberService";
import { getProfile } from "./authSlice";
import toast from "react-hot-toast";

const initialState = {
  virtualNumberCountries: [],
  virtualNumberPrices: [],
  virtualNumberProviders: [],
  selectedCountry: null,
  selectedProvider: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  },
  search: {
    countrySearch: "",
    serviceSearch: "",
  },
  myNumbers: [],
  isLoading: false,
  syncLoading: false,
  purchaseLoading: false,
  myNumbersLoading: false,
  cancelLoading: false,
  bulkLoading: false,
  providersLoading: false,
  error: null,
};

// Thunks
export const syncVirtualNumberPrices = createAsyncThunk(
  "admin/syncVirtualNumberPrices",
  async (providerId = "herosms", { dispatch, rejectWithValue }) => {
    try {
      const response = await adminService.syncVirtualNumberPrices(providerId);
      toast.success("Virtual numbers synced successfully!");

      // Refresh data after successful sync
      dispatch(fetchVirtualNumberCountries({ provider: providerId }));
      dispatch(fetchVirtualNumberPrices({ provider: providerId }));
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to sync virtual numbers",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchVirtualNumberCountries = createAsyncThunk(
  "admin/fetchVirtualNumberCountries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getVirtualNumberCountries(params);
      return response;
    } catch (error) {
      toast.error("Failed to fetch countries");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchPublicVirtualNumberCountries = createAsyncThunk(
  "virtualNumbers/fetchPublicVirtualNumberCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await virtualNumberService.getCountries();
      return response;
    } catch (error) {
      toast.error("Failed to fetch countries");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchPublicVirtualNumberPrices = createAsyncThunk(
  "virtualNumbers/fetchPublicVirtualNumberPrices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await virtualNumberService.getServicesByCountry(
        params.countryId,
        {
          page: params.page,
          limit: params.limit,
          serviceSearch: params.serviceSearch,
        },
      );
      return response;
    } catch (error) {
      toast.error("Failed to fetch virtual number services");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchVirtualNumberPrices = createAsyncThunk(
  "admin/fetchVirtualNumberPrices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getVirtualNumberPrices(params);
      return response;
    } catch (error) {
      toast.error("Failed to fetch prices");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const toggleVirtualNumberProvider = createAsyncThunk(
  "admin/toggleVirtualNumberProvider",
  async ({ providerId, isEnabled }, { dispatch, rejectWithValue }) => {
    try {
      const response = await adminService.toggleVirtualNumberProvider(
        providerId,
        isEnabled,
      );
      toast.success("Provider status updated");
      // Refresh providers list
      await dispatch(fetchVirtualNumberProviders()).unwrap();
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to toggle provider");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateVirtualNumberPrice = createAsyncThunk(
  "admin/updateVirtualNumberPrice",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateVirtualNumberPrice(id, data);
      toast.success("Price updated successfully");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update price");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const bulkUpdateVirtualNumberPrices = createAsyncThunk(
  "admin/bulkUpdateVirtualNumberPrices",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await adminService.bulkUpdateVirtualNumberPrices(payload);
      toast.success(`${response.updated} services updated`);
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to bulk update prices",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const purchaseVirtualNumber = createAsyncThunk(
  "virtualNumbers/purchase",
  async ({ serviceCode, countryId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await virtualNumberService.purchaseVirtualNumber(
        serviceCode,
        countryId,
      );
      await dispatch(getProfile())
        .unwrap()
        .catch(() => null);
      toast.success("Virtual number purchased successfully!");
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to purchase virtual number",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchMyNumbers = createAsyncThunk(
  "virtualNumbers/fetchMyNumbers",
  async (status = "all", { rejectWithValue }) => {
    try {
      const response = await virtualNumberService.getMyNumbers(status);
      return response;
    } catch (error) {
      toast.error("Failed to fetch your numbers");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchSmsCode = createAsyncThunk(
  "virtualNumbers/fetchSmsCode",
  async (purchaseId, { rejectWithValue }) => {
    try {
      const response = await virtualNumberService.getSmsCode(purchaseId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const cancelVirtualNumber = createAsyncThunk(
  "virtualNumbers/cancel",
  async (purchaseId, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await virtualNumberService.cancelVirtualNumber(purchaseId);
      toast.success("Virtual number cancelled and refunded!");
      // Refresh my numbers after cancellation
      await dispatch(fetchMyNumbers())
        .unwrap()
        .catch(() => null);
      // Refresh user profile to update balance
      await dispatch(getProfile())
        .unwrap()
        .catch(() => null);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to cancel virtual number";
      toast.error(errorMessage);
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchVirtualNumberProviders = createAsyncThunk(
  "admin/fetchVirtualNumberProviders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getVirtualNumberProviders();
      return response;
    } catch (error) {
      toast.error("Failed to fetch providers");
      return rejectWithValue(error.response?.data);
    }
  },
);

const virtualNumberSlice = createSlice({
  name: "virtualNumbers",
  initialState,
  reducers: {
    setSelectedCountry: (state, action) => {
      state.selectedCountry = action.payload;
      state.pagination.page = 1; // Reset to first page when country changes
    },
    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
      state.selectedCountry = null;
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setCountrySearch: (state, action) => {
      state.search.countrySearch = action.payload;
    },
    setServiceSearch: (state, action) => {
      state.search.serviceSearch = action.payload;
      state.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sync prices
      .addCase(syncVirtualNumberPrices.pending, (state) => {
        state.syncLoading = true;
      })
      .addCase(syncVirtualNumberPrices.fulfilled, (state, action) => {
        state.syncLoading = false;
      })
      .addCase(syncVirtualNumberPrices.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload;
      })
      // Fetch countries
      .addCase(fetchVirtualNumberCountries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchVirtualNumberCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.virtualNumberCountries = action.payload.countries || [];
        // Auto-select first country
        if (
          action.payload.countries &&
          action.payload.countries.length > 0 &&
          !state.selectedCountry
        ) {
          state.selectedCountry = action.payload.countries[0]._id;
        }
      })
      .addCase(fetchVirtualNumberCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPublicVirtualNumberCountries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPublicVirtualNumberCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.virtualNumberCountries = action.payload.countries || [];
        if (
          action.payload.countries &&
          action.payload.countries.length > 0 &&
          !state.selectedCountry
        ) {
          state.selectedCountry = action.payload.countries[0]._id;
        }
      })
      .addCase(fetchPublicVirtualNumberCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch prices
      .addCase(fetchVirtualNumberPrices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchVirtualNumberPrices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.virtualNumberPrices = action.payload.prices || [];
        state.pagination.page = action.payload.page || 1;
        state.pagination.total = action.payload.total || 0;
        state.pagination.pages = action.payload.pages || 0;
      })
      .addCase(fetchVirtualNumberPrices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch public virtual number prices
      .addCase(fetchPublicVirtualNumberPrices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPublicVirtualNumberPrices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.virtualNumberPrices = action.payload.services || [];
        state.pagination.page = action.payload.page || 1;
        state.pagination.total = action.payload.total || 0;
        state.pagination.pages = action.payload.pages || 0;
      })
      .addCase(fetchPublicVirtualNumberPrices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(bulkUpdateVirtualNumberPrices.pending, (state) => {
        state.bulkLoading = true;
      })
      .addCase(bulkUpdateVirtualNumberPrices.fulfilled, (state, action) => {
        state.bulkLoading = false;
        if (action.payload.results && Array.isArray(action.payload.results)) {
          action.payload.results.forEach((updated) => {
            const index = state.virtualNumberPrices.findIndex(
              (p) => p._id === updated._id,
            );
            if (index !== -1) {
              state.virtualNumberPrices[index] = updated;
            }
          });
        }
      })
      .addCase(bulkUpdateVirtualNumberPrices.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.payload;
      })
      // Update price
      .addCase(updateVirtualNumberPrice.fulfilled, (state, action) => {
        const index = state.virtualNumberPrices.findIndex(
          (p) => p._id === action.payload.price._id,
        );
        if (index !== -1) {
          state.virtualNumberPrices[index] = action.payload.price;
        }
      })
      // Purchase virtual number
      .addCase(purchaseVirtualNumber.pending, (state) => {
        state.purchaseLoading = true;
      })
      .addCase(purchaseVirtualNumber.fulfilled, (state, action) => {
        state.purchaseLoading = false;
      })
      .addCase(purchaseVirtualNumber.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.error = action.payload;
      })
      // Fetch my numbers
      .addCase(fetchMyNumbers.pending, (state) => {
        state.myNumbersLoading = true;
      })
      .addCase(fetchMyNumbers.fulfilled, (state, action) => {
        state.myNumbersLoading = false;
        state.myNumbers = action.payload.data || [];
      })
      .addCase(fetchMyNumbers.rejected, (state, action) => {
        state.myNumbersLoading = false;
        state.error = action.payload;
      })
      // Fetch SMS code
      .addCase(fetchSmsCode.fulfilled, (state, action) => {
        const index = state.myNumbers.findIndex(
          (num) => num._id === action.meta.arg,
        );
        if (index !== -1) {
          const updatedStatus = action.payload.data.status;
          if (updatedStatus !== "active") {
            state.myNumbers = state.myNumbers.filter(
              (num) => num._id !== action.meta.arg,
            );
          } else {
            state.myNumbers[index] = {
              ...state.myNumbers[index],
              smsCode: action.payload.data.smsCode,
              smsText: action.payload.data.smsText,
              smsReceivedAt: action.payload.data.smsReceivedAt,
              status: action.payload.data.status,
              lastCheckedAt: new Date().toISOString(),
            };
          }
        }
      })
      // Cancel virtual number
      .addCase(cancelVirtualNumber.pending, (state) => {
        state.cancelLoading = true;
      })
      .addCase(cancelVirtualNumber.fulfilled, (state, action) => {
        state.cancelLoading = false;
        // Remove the cancelled number from myNumbers
        const purchaseId = action.meta.arg;
        state.myNumbers = state.myNumbers.filter(
          (num) => num._id !== purchaseId,
        );
      })
      .addCase(cancelVirtualNumber.rejected, (state, action) => {
        state.cancelLoading = false;
        state.error = action.payload;
      })
      // Fetch providers
      .addCase(fetchVirtualNumberProviders.pending, (state) => {
        state.providersLoading = true;
      })
      .addCase(fetchVirtualNumberProviders.fulfilled, (state, action) => {
        state.providersLoading = false;
        state.virtualNumberProviders = action.payload.providers || [];
        // Auto-select first provider
        if (
          action.payload.providers &&
          action.payload.providers.length > 0 &&
          !state.selectedProvider
        ) {
          state.selectedProvider = action.payload.providers[0].providerId;
        }
      })
      .addCase(fetchVirtualNumberProviders.rejected, (state, action) => {
        state.providersLoading = false;
        state.error = action.payload;
      })
      // Toggle provider
      .addCase(toggleVirtualNumberProvider.fulfilled, (state, action) => {
        // Providers list is refreshed in the thunk, so no need to update here
      });
  },
});

export const {
  setSelectedCountry,
  setSelectedProvider,
  setPage,
  setCountrySearch,
  setServiceSearch,
} = virtualNumberSlice.actions;
export default virtualNumberSlice.reducer;
